import { GraphEdge, GraphVertex } from '../types';
import getVertexType from './getVertexType';

interface TypeMap {
  [type: string]: { packedVerticesIds: string[]; class: string };
}

export interface Pack {
  type: string;
  class: string;
  packedVerticesIds: string[];
  parentVertexId: string;
}

const mapTypesToVertexIds = (
  allVertices: GraphVertex[],
  vertexIds: string[]
) => {
  const typeMap = {} as TypeMap;

  vertexIds.forEach((id) => {
    const vertex = allVertices.find((v) => v.id === id);
    if (vertex) {
      const type = getVertexType(vertex);
      typeMap[type] = typeMap[type]
        ? {
            packedVerticesIds: [...typeMap[type].packedVerticesIds, id],
            class: vertex?.entity?._class?.[0] || 'unknown',
          }
        : {
            packedVerticesIds: [id],
            class: vertex?.entity?._class?.[0] || 'unknown',
          };
    }
  });

  return typeMap;
};

const generatePacksFromTypeMap = (typeMap: TypeMap, parentVertexId: string) => {
  const packs: Pack[] = Object.keys(typeMap)
    .map((k) => ({
      packedVerticesIds: typeMap[k].packedVerticesIds,
      type: k,
      parentVertexId: parentVertexId,
      class: typeMap[k].class,
    }))
    .filter((p) => p.packedVerticesIds.length > 1);

  return packs;
};

const vertexIsUniqueAcrossPacks = (id: string, packs: Pack[]) =>
  packs.find((p) => p.packedVerticesIds.indexOf(id) >= 0) === undefined;

const vertexIsUniqueInCurrentPack = (id: string, index: number, pack: Pack) =>
  pack.packedVerticesIds.indexOf(id) === index;

const removeDuplicatePackedVertices = (packs: Pack[]) => {
  const dedupedPacks: Pack[] = [];
  packs.forEach((p) => {
    dedupedPacks.push({
      ...p,
      packedVerticesIds: p.packedVerticesIds.filter(
        (id, i) =>
          vertexIsUniqueAcrossPacks(id, dedupedPacks) &&
          vertexIsUniqueInCurrentPack(id, i, p)
      ),
    });
  });
  return dedupedPacks.filter((p) => p.packedVerticesIds.length > 1);
};

const packVertices = (vertices: GraphVertex[], edges: GraphEdge[]) => {
  let packs: Pack[] = [];
  edges.forEach((edge) => {
    // If this parent id has been processed, continue
    if (packs.filter((p) => edge.fromVertexId === p.parentVertexId).length) {
      return;
    }

    const childVertexIds = edges
      .filter((e) => e.fromVertexId === edge.fromVertexId)
      .map((e) => e.toVertexId);

    const typeMap = mapTypesToVertexIds(vertices, childVertexIds);
    const newPacks = generatePacksFromTypeMap(typeMap, edge.fromVertexId);

    packs = packs.concat(newPacks);
  });

  const dedupedPacks = removeDuplicatePackedVertices(packs);
  return dedupedPacks;
};

export default packVertices;
